import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { Link2, Trash2 } from 'lucide-react';
import mcpKeysService from '../../services/mcpKeysService';
import { describeApiError, isNotFound } from '../../utils/apiError';
import { formatWhen } from '../shared/RunDetail/runDetailFormat';
import { formatDate, roughLastUsed } from '../shared/PlatformMcpActivity/platformMcpFormat';
import InfoTip from '../shared/InfoTip';
import { PLATFORM_MCP_COPY } from '../../constants/platformMcpCopy';
import pageStyles from '../MyLLMKeys/MyLLMKeys.module.scss';
import styles from './PlatformMcp.module.scss';

const keyLabel = (k) => k?.name || `…${k?.last4}`;

/**
 * The user's connection keys. Listing and revoking work without the
 * `platform_mcp` permission, so a user who lost access can still clean up.
 * The page owns the list so it refreshes after a create.
 */
const MyKeysTab = ({ keys, loading, error, onReload, notify }) => {
  const [showRevoked, setShowRevoked] = useState(false);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  const revokedCount = keys.filter((k) => !k.active).length;
  const visibleKeys = showRevoked ? keys : keys.filter((k) => k.active);

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await mcpKeysService.revokeKey(revokeTarget.id);
      notify(`Revoked "${keyLabel(revokeTarget)}".`, 'success');
    } catch (err) {
      notify(
        isNotFound(err) ? PLATFORM_MCP_COPY.keyGone : describeApiError(err, "Couldn't revoke the key."),
        'error'
      );
    } finally {
      setRevoking(false);
      setRevokeTarget(null);
      onReload();
    }
  };

  return (
    <Box className={styles.tabBody}>
      <Box className={styles.listHeader}>
        <Typography className={styles.cardTitle}>My keys</Typography>
        {revokedCount > 0 && (
          <FormControlLabel
            control={
              <Switch size="small" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} />
            }
            label={`${PLATFORM_MCP_COPY.showRevoked} (${revokedCount})`}
          />
        )}
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      {loading && keys.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
          <CircularProgress color="primary" />
        </Box>
      ) : visibleKeys.length === 0 ? (
        <Box className={pageStyles.emptyState}>
          <Link2 className={pageStyles.emptyIcon} size={48} />
          <Typography variant="h6">{PLATFORM_MCP_COPY.noKeys}</Typography>
          <Typography variant="body2">{PLATFORM_MCP_COPY.noKeysHint}</Typography>
        </Box>
      ) : (
        <Box className={pageStyles.tableCard}>
          <Box className={`${styles.keyRow} ${pageStyles.headRow}`}>
            <span>Name</span>
            <span>Key</span>
            <span>Created</span>
            <span className={styles.headCell}>
              Last used <InfoTip title={PLATFORM_MCP_COPY.lastUsedTip} size={13} />
            </span>
            <span>Status</span>
            <span className={styles.actionsHead}>Actions</span>
          </Box>
          {visibleKeys.map((k) => (
            <Box key={k.id} className={`${styles.keyRow} ${k.active ? '' : styles.revokedRow}`}>
              <span className={styles.keyName}>{k.name || PLATFORM_MCP_COPY.unnamedKey}</span>
              <span className={styles.keyLast4}>…{k.last4}</span>
              <Tooltip title={formatWhen(k.created_at)}>
                <span>{formatDate(k.created_at)}</span>
              </Tooltip>
              <Tooltip title={k.last_used_at ? formatWhen(k.last_used_at) : ''}>
                <span className={k.last_used_at ? '' : styles.muted}>{roughLastUsed(k.last_used_at)}</span>
              </Tooltip>
              <span>
                {k.active ? (
                  <span className={`${pageStyles.badge} ${pageStyles.success}`}>Active</span>
                ) : (
                  <Tooltip title={k.revoked_at ? `Revoked ${formatWhen(k.revoked_at)}` : ''}>
                    <span className={`${pageStyles.badge} ${pageStyles.neutral}`}>Revoked</span>
                  </Tooltip>
                )}
              </span>
              <span className={styles.actionsCell}>
                {k.active && (
                  <Button
                    size="small"
                    className={styles.revokeBtn}
                    startIcon={<Trash2 size={14} />}
                    onClick={() => setRevokeTarget(k)}
                  >
                    Revoke
                  </Button>
                )}
              </span>
            </Box>
          ))}
        </Box>
      )}

      <Dialog
        open={!!revokeTarget}
        onClose={revoking ? undefined : () => setRevokeTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Revoke “{keyLabel(revokeTarget)}”?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {PLATFORM_MCP_COPY.revokeConfirm}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRevoke}
            disabled={revoking}
            startIcon={revoking ? <CircularProgress size={14} color="inherit" /> : null}
          >
            {revoking ? 'Revoking…' : 'Revoke'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyKeysTab;
